import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';
import { TheraSelect } from '../../theraSelect/TheraSelect';

export const AbvSapReconPerspective = () => {
  const entityPerspectiveRef = useRef();
  const theraSelectRef = useRef();

  const views = [
    {
      name: 'Payments',
      dims: ['PAID_PERIOD', 'CHANNEL', 'PROD', 'EARNED_PERIOD'],
      kpis: ['SAP_PYMT', 'SAP_PYMT_ADJ', 'TOTAL_SAP_PYMT', 'KR_PYMT', 'KR_PYMT_ADJ', 'TOTAL_KR_PYMT', 'ADJ_DELTA_AMT'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['LIFETIME', 'YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR'],
      },
    },
  ];

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'discrepancy',
      icon: fas.faExclamationTriangle,
      colors: [null, 'red', 'green'],
      toolTips: ['Discrepancies and no discrepancies', 'Discrepancies', 'No discrepancies'],
    },
    {
      key: 'adjusted',
      icon: fas.faPencil,
      colors: [null, 'green', 'grey'], // TODO: WHAT COLOR SHOULD NO ADJUSTMENTS BE?
      toolTips: ['Adjusted and no adjustments', 'Adjusted', 'No adjustments'],
    },
  ]);

  const handleViewReset = useCallback(() => {
    onViewReset();
    theraSelectRef.current.reset();
  }, [onViewReset]);

  const events = {
    onDimFilterChange: ({ dimCode }) => {
      if (dimCode === 'PROD') {
        theraSelectRef.current.reset();
      }
    },
  };

  const augButtons = (buttons) => {
    buttons.unshift(
      <TheraSelect entityPerspectiveRef={entityPerspectiveRef} ref={theraSelectRef} />,
      ...filterButtons,
      <TitleButtonDivider key="filterButtonDivider" />,
    );
  };

  const augReqParam = useCallback(
    (reqParam) => {
      // Discrepancy
      const discrepancyButtonState = filterButtonStages.discrepancy;
      if (discrepancyButtonState > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          discrepancyButtonState === 1 ? ExprUtils.whereParams('discrepancy', 1) : ExprUtils.isNot('discrepancy', 1),
        );
      }

      // Adjusted
      const adjustedButtonState = filterButtonStages.adjusted;
      if (adjustedButtonState > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          adjustedButtonState === 1 ? ExprUtils.whereParams('adjusted', 1) : ExprUtils.isNot('adjusted', 1),
        );
      }
    },
    [filterButtonStages],
  );

  const rowIconsFn = (data) => {
    let rowIcons = [];

    // Closed
    if (data.closed) {
      rowIcons.push(<RowIcon key="closed" icon={fas.faCheckCircle} color="green" toolTip="Period is closed" />);
    }

    // Discrepancy row icons
    if (data.discrepancy === 1) {
      rowIcons.push(<RowIcon key="discrepancy" icon={fas.faExclamationTriangle} color="red" toolTip="Discrepancy" />);
    } else if (data.discrepancy > 0) {
      rowIcons.push(
        <RowIcon
          key="containsDiscrepancy"
          icon={fas.faExclamationTriangle}
          color="orange"
          toolTip="Contains a discrepancy"
        />,
      );
    }

    // Adjusted row icons
    if (data.adjusted === 1) {
      rowIcons.push(<RowIcon key="adjusted" icon={fas.faPencil} color="green" toolTip="Adjusted" />);
    } else if (data.adjusted > 0) {
      // Light green
      rowIcons.push(
        <RowIcon key="containsAdjusted" icon={fas.faPencil} color="#90ee90" toolTip="Contains an adjustment" />,
      );
    }

    return rowIcons;
  };

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    let pymtSubMenu = [
      {
        name: 'SAP Payments',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'SAP_PYMT', 'SAP Payments'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot SAP Payments',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'SAP_PYMT', 'SAP Payments'),
        icon: fas.faTableColumns,
      },
      {
        name: 'Kernel Payments',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'KR_PYMT', 'Kernel Payments'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Kernel Payments',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'KR_PYMT', 'Kernel Payments'),
        icon: fas.faTableColumns,
      },
      {
        name: 'Compare SAP and Kernel Payments',
        action: () =>
          entityPerspectiveRef.current.navigateToLookupViewer(
            params,
            'SAP_PYMT',
            'SAP Payments',
            'KR_PYMT',
            'Kernel Payments',
          ),
        icon: fas.faBalanceScale,
      },
    ];

    let pymtAdjSubMenu = [
      {
        name: 'SAP Payment Adjustments',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'SAP_PYMT_ADJ', 'SAP Payment Adjustments'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot SAP Payment Adjustments',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'SAP_PYMT_ADJ', 'SAP Payment Adjustments'),
        icon: fas.faTableColumns,
      },
      {
        name: 'Kernel Payment Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToViewer(params, 'KR_PYMT_ADJ', 'Kernel Payment Adjustments'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Kernel Payment Adjustments',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'KR_PYMT_ADJ', 'Kernel Payment Adjustments'),
        icon: fas.faTableColumns,
      },
    ];

    contextMenus.push(
      {
        name: 'Payment Amounts',
        subMenu: pymtSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Payment Adjustments',
        subMenu: pymtAdjSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      rowIconsFn={rowIconsFn}
      augButtons={augButtons}
      augReqParam={augReqParam}
      onViewReset={handleViewReset}
      events={events}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
